import { CreditCard } from "@/api/credit-cards/credit-cards";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import { creditCardKeys } from "@/features/credit-cards/keys";
import useDeleteCreditCard from "@/features/credit-cards/useDeleteCreditCard";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import ActivityRowItem from "../../../../../components/_module/home/activityRowItem";
import GreenCard from "./CreditCardDisplay";
import {
	Button,
	buttonVariants,
} from "../../../../../components/ui/button/button";
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
} from "../../../../../components/ui/carousel";
import { ArrowDownIcon } from "../../../../../components/ui/icons";
import CardInfo from "./CardInformation";
import { Link, useLoaderData } from "@tanstack/react-router";
import useGetCCLastTransaction from "@/features/transactions/useGetCCLastTransaction";
import { cn } from "@/lib/utils";

interface CardViewProps {
	creditCards: CreditCard[];
}

const CardView: React.FC<CardViewProps> = ({ creditCards: cardList }) => {
	const [selectedCardId, setSelectedCardId] = useState(cardList[0].id);
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);
	const queryClient = useQueryClient();
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const { toast } = useToast();
	const { user } = useAuth();
	const { location_id } = useLoaderData({ from: "__root__" });
	const deleteCreditCardMutation = useDeleteCreditCard();
	const selectedCard = cardList.find((card) => card.id === selectedCardId);
	const { data: lastTransaction } = useGetCCLastTransaction({
		cardId: selectedCardId,
		guestId: user.id,
		locationId: location_id,
	});

	if (!selectedCard) {
		throw new Error("No card found");
	}

	function handleDeleteCard(cardId: number) {
		deleteCreditCardMutation.mutate(
			{
				creditCardId: cardId,
				guestId: user.id,
				locationId: location_id,
			},
			{
				onSuccess: () => {
					toast({
						variant: "default",
						title: "Success",
						description: "Card was successfully deleted",
					});
					setDeleteModalOpen(false);
					const cardIndex = cardList.findIndex((card) => card.id === cardId);
					const nextCard = cardList[cardIndex + 1];
					const prevCard = cardList[cardIndex - 1];

					if (nextCard) {
						setSelectedCardId(nextCard.id);
					} else {
						if (prevCard) {
							setSelectedCardId(prevCard.id);
						}
					}

					queryClient.refetchQueries({
						queryKey: creditCardKeys.list({ guestId: user.id }),
					});
				},
				onError: (error) => {
					toast({
						variant: "destructive",
						title: "Error",
						description: error.message,
					});
				},
			},
		);
	}

	React.useEffect(() => {
		if (!api) return;

		api.on("select", () => {
			const index = api.selectedScrollSnap();
			setCurrent(index);
			setSelectedCardId(cardList[index]?.id || 0);
		});
	}, [api, cardList]);

	return (
		<div className="h-full">
			<div className="max-w-md mx-auto">
				<div className="transition-all duration-300 ease-in-out overflow-hidden px-4">
					<div className="relative">
						<Carousel
							data-testid="carousel"
							opts={{
								align: "start",
								loop: true,
								containScroll: "trimSnaps",
							}}
							className="w-full"
							setApi={setApi}
						>
							<CarouselContent data-testid="carousel-content" className="-ml-2">
								{cardList.map((item, index) => (
									<CarouselItem
										key={item.id}
										className={cn(
											"pl-2",
											cardList.length === 1 ? "basis-[100%]" : "basis-[85%]",
										)}
									>
										<div
											className={`p-1 transition-opacity duration-300 select-none ${
												index !== current ? "opacity-50" : "opacity-100"
											}`}
										>
											<GreenCard card={item} />
										</div>
									</CarouselItem>
								))}
							</CarouselContent>
						</Carousel>
						<div className="flex justify-center gap-2 mt-4">
							{cardList.map((card, index) => (
								<Button
									size="compact"
									key={card.id}
									className={`w-2 h-2 rounded-full transition-all duration-300 ${
										index === current ? "bg-primary-600 " : "bg-gray-300"
									}`}
									onClick={() => api?.scrollTo(index)}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
			<div className="px-6 py-7">
				<CardInfo
					deleteCardStatus={deleteCreditCardMutation.status}
					onDeleteConfirm={(cardId) => {
						handleDeleteCard(cardId);
					}}
					card={selectedCard}
					deleteModalOpen={deleteModalOpen}
					setDeleteModalOpen={setDeleteModalOpen}
				/>
			</div>
			<div className="px-6 py-4">
				<Link
					to={"/main/deposit/$cardId/amount"}
					params={{
						cardId: selectedCard.id.toString(),
					}}
					className={buttonVariants({
						variant: "secondary",
						className: "w-full",
					})}
				>
					Charge this card
					<ArrowDownIcon className="w-4 h-4 -rotate-90 fill-secondaryText" />
				</Link>
			</div>
			{lastTransaction && (
				<div className="px-6 mt-7">
					<h2 className="mb-6 text-lg font-semibold text-textDark">
						Recent Activity
					</h2>
					<ActivityRowItem transaction={lastTransaction} />
				</div>
			)}
			<div className="pt-20"></div>
		</div>
	);
};

export default CardView;
